import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Ide.css';
import '../Devices/ESPInstall';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { githubLight } from '@uiw/codemirror-theme-github';
import { useDispatch, useSelector } from 'react-redux';
import { uploadFile, generateFirmware, getFirmware } from '../../api/devices';
import debounce from 'lodash.debounce';
import ESPInstall from '../Devices/ESPInstall';
import { setFirmware } from '../../redux/firmware';
import { toast } from 'react-toastify';

function Ide({ handleFileChange, file, deviceId }) {
  const dispatch = useDispatch();
  const [value, setValue] = useState('');
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toastId, setToastId] = useState(null);
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef(null);

  const fetchFileContent = async (file) => {
    try {
      const response = await fetch(file);
      if (!response.ok) throw new Error('Network response was not ok');
      const text = await response.text();
      setValue(text);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (file && typeof file === 'string' && file.trim() !== '') {
      fetchFileContent(file);
    }
  }, [file]);

  // Start fake progress
  const startFakeProgress = () => {
    setProgress(0); // Reset to 0 first
    clearInterval(progressIntervalRef.current);

    // Small delay to ensure state is updated
    setTimeout(() => {
      progressIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressIntervalRef.current);
            return 90;
          }
          const increment = prev < 50 ? 8 : 4;
          return Math.min(prev + increment, 90);
        });
      }, 600);
    }, 100);
  };

  // Complete progress (when we get WebSocket response)
  const completeProgress = () => {
    clearInterval(progressIntervalRef.current);
    setProgress(100);
    setTimeout(() => {
      setProgress(0);
    }, 1000);
  };

  useEffect(() => {
    const handleFirmwareGenerationMessage = (event) => {
      try {
        const data = event.detail;

        if (data.type === 'generate_firmware') {
          if (toastId) {
            completeProgress(); // Complete the progress bar

            if (data.status === 'success') {
              setTimeout(() => {
                setIsLoading(false);
                setToastId(null);
                getFirmware(deviceId).then((firmware) => {
                  dispatch(setFirmware(firmware));
                });
              }, 500);
            } else if (data.status === 'failed') {
              setTimeout(() => {
                setIsLoading(false);
                setToastId(null);
              }, 500);
            }
          }
        }
      } catch (error) {
        console.error('Error handling WebSocket message:', error);
        // If WebSocket handler itself fails, show error
        if (toastId) {
          toast.update(toastId, {
            render: 'Ошибка соединения',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
          setToastId(null);
          setIsLoading(false);
        }
      }
    };

    window.addEventListener(
      'websocket_message',
      handleFirmwareGenerationMessage
    );

    return () => {
      window.removeEventListener(
        'websocket_message',
        handleFirmwareGenerationMessage
      );
      clearInterval(progressIntervalRef.current);
    };
  }, [toastId, deviceId, dispatch]);

  // Debounced upload function
  const debouncedUploadFile = useCallback(
    debounce((content) => {
      uploadFile(content, deviceId)
        .then((response) => {
          console.log('File uploaded successfully:', response);
        })
        .catch((error) => {
          console.error('Error uploading file:', error);
        });
    }, 1000),
    [deviceId]
  );

  const onChange = useCallback(
    (newValue) => {
      setValue(newValue);
      debouncedUploadFile(newValue);
    },
    [debouncedUploadFile]
  );

  const handleSave = () => {
    debouncedUploadFile.cancel();
    uploadFile(value, deviceId)
      .then((response) => {
        console.log('File saved successfully:', response);

        if (!toast.isActive('file-saved')) {
          toast.success('Файл сохранен', {
            autoClose: 2000,
            toastId: 'file-saved',
          });
        }
      })
      .catch((error) => {
        console.error('Error saving file:', error);

        if (!toast.isActive('save-error')) {
          toast.error('Ошибка сохранения файла', {
            autoClose: 5000,
            toastId: 'save-error',
          });
        }
      });
  };

  const handleGenerateCode = async () => {
    if (!prompt.trim()) {
      toast.error('Введите описание для генерации кода');
      return;
    }

    try {
      setIsLoading(true);
      startFakeProgress(); // Start the fake progress bar

      // Create a reference to track updates
      const ProgressToast = ({ progress }) => (
        <div className="w-full">
          <div className="text-sm font-medium mb-2">Генерация прошивки...</div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-xs text-gray-500 mt-1 text-right">
            {progress}%
          </div>
        </div>
      );

      // Create initial toast with current progress (should be 0)
      const id = toast(<ProgressToast progress={progress} />, {
        position: 'top-right',
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        isLoading: true,
      });

      setToastId(id);

      await generateFirmware(deviceId, prompt);
    } catch (error) {
      setIsLoading(false);
      clearInterval(progressIntervalRef.current);
      setProgress(0);

      // If there's a progress toast, update it to show error
      if (toastId) {
        toast.update(toastId, {
          render: 'Ошибка при запуске генерации прошивки',
          type: 'error',
          isLoading: false,
          autoClose: 5000, // Show for 5 seconds
        });
        setToastId(null);
      } else {
        // If no toast exists, create a new error toast
        toast.error('Ошибка при запуске генерации прошивки', {
          autoClose: 5000,
        });
      }
    }
  };

  useEffect(() => {
    if (toastId && progress >= 0) {
      // Changed to >= 0 to catch initial updates
      const ProgressToast = ({ progress }) => (
        <div className="w-full">
          <div className="text-sm font-medium mb-2">Генерация прошивки...</div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-xs text-gray-500 mt-1 text-right">
            {progress}%
          </div>
        </div>
      );

      toast.update(toastId, {
        render: <ProgressToast progress={progress} />,
      });
    }
  }, [progress, toastId]);

  // Handle Download as File
  const handleDownload = () => {
    const blob = new Blob([value], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file && file?.name ? file.name : 'iopp_firmare.ino';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedUploadFile.cancel();
      clearInterval(progressIntervalRef.current);
      if (toastId) {
        toast.dismiss(toastId);
      }
    };
  }, [debouncedUploadFile, toastId]);

  return (
    <>
      <div className="mt-6 space-y-2">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleSave}
            className="w-full items-center rounded-xl border bg-green-100 px-4 py-1 text-center hover:bg-green-200 lg:w-fit"
          >
            Сохранить файл
          </button>
          <div className="w-full items-center rounded-xl border bg-zinc-100 px-4 py-1 text-center hover:bg-zinc-200 lg:w-fit">
            <input
              type="file"
              id="firmwareFile"
              className="hidden"
              onChange={handleFileChange}
            />
            <label htmlFor="firmwareFile" className="cursor-pointer">
              Загрузить файл
            </label>
          </div>

          <button
            onClick={handleDownload}
            className="w-full items-center rounded-xl border bg-blue-50 px-4 py-1 text-center hover:bg-blue-100 lg:w-fit"
          >
            Скачать файл
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Хочу подключить датчик DHT11"
            className="w-full rounded-xl border bg-white px-4 py-2"
          />
          <button
            onClick={handleGenerateCode}
            disabled={isLoading || !prompt.trim()}
            className="w-full text-nowrap items-center rounded-xl border border-gray-300 bg-green-100 px-4 py-2 text-center hover:bg-green-200 disabled:bg-gray-100 disabled:cursor-not-allowed lg:w-fit"
          >
            {isLoading ? 'Генерация...' : 'Сгенерировать прошивку'}
          </button>
        </div>
        <hr />

        {/* CodeMirror Editor */}
        <CodeMirror
          theme={githubLight}
          value={value}
          height="100%"
          className="codemirror-editor w-full overflow-x-auto text-sm"
          extensions={[javascript({ jsx: true })]}
          onChange={onChange}
        />
      </div>
    </>
  );
}

export default Ide;
